import {createSubscriptionState} from './createSubscriptionState';
import {openSocket} from './openSocket';
import {stopSubscription} from './stopSubscription';
import type {NotificationSubscription, SubscriptionDeps} from './subscriptionTypes';

/**
 * Subscription orchestrator. Read this file first — it shows the lifecycle in
 * one screen:
 *
 *   1. `start()` opens the first socket and arms the self-heal loop.
 *      The loop lives inside the socket handlers (see `openSocket.ts`):
 *        - `connection_ack` → `registerSubscription` (send start frame, arm keep-alive)
 *        - `start_ack`      → `clearReauthAttempts`
 *        - `data`           → `appendNotification` (bounded ring buffer)
 *        - `error`          → `reportErrorAndMaybeRefresh` → `startAuthRefresh` → `applyRefreshResult`
 *        - `complete`/`ka`/timeouts/close → `scheduleReconnect`
 *        - missing/malformed `APPSYNC_URL` → `stopForMisconfiguration` (no retry)
 *
 *   2. `stop()` is called on unmount — it sends the `stop` frame and closes the
 *      socket cleanly.
 */
export const createNotificationSubscription = (
  deps: SubscriptionDeps
): NotificationSubscription => {
  const state = createSubscriptionState();
  state.reopenSocket = () => openSocket(state, deps);

  return {
    start: () => openSocket(state, deps),
    stop: () => stopSubscription(state, deps),
  };
};
